import { BackendApiClient } from '../../auth/api/backend-client';
import { BackendValidationError } from '../../auth/api/investor';
import type { ScopeContext } from '../../auth/model/types';
import { requireList } from '@/shared/lib/response-shape';

const backendClient = new BackendApiClient();

export type NotificationType = 'broker-order';
export type NotificationSubscriptionStatus = 'active' | 'canceled';

export interface NotificationSubscription {
  id: string;
  user_id: string;
  types: NotificationType[];
  status: NotificationSubscriptionStatus;
  created: string;
}

export interface NotificationSubscriptionResponse {
  subscriptions: NotificationSubscription[];
  cursor?: string;
}

export async function getNotificationSubscriptions(
  sessionId: string,
  scopeContext: ScopeContext,
): Promise<NotificationSubscriptionResponse> {
  const response = await backendClient.get('/notification-subscription', sessionId, scopeContext);
  if (response.success && response.data) {
    const subscriptions = requireList<NotificationSubscription>(
      response.data,
      'subscriptions',
      'The notification-subscription response carries no readable subscription list',
    );
    return { ...(response.data as NotificationSubscriptionResponse), subscriptions };
  }
  throw new BackendValidationError(response.error || 'Failed to fetch notification subscriptions');
}

export async function createNotificationSubscription(
  sessionId: string,
  scopeContext: ScopeContext,
  body: { types: NotificationType[] },
): Promise<NotificationSubscriptionResponse> {
  const response = await backendClient.post(
    '/notification-subscription',
    sessionId,
    { subscriptions: [body] },
    scopeContext,
  );
  if (response.success && response.data) {
    return response.data as NotificationSubscriptionResponse;
  }
  throw new BackendValidationError(response.error || 'Failed to create notification subscription');
}

export async function patchNotificationSubscription(
  sessionId: string,
  scopeContext: ScopeContext,
  subscriptionId: string,
  body: { types?: NotificationType[]; status?: NotificationSubscriptionStatus },
): Promise<{ subscription: NotificationSubscription }> {
  const response = await backendClient.patch(
    `/notification-subscription/${subscriptionId}`,
    sessionId,
    body,
    scopeContext,
  );
  if (response.success && response.data) {
    return response.data as { subscription: NotificationSubscription };
  }
  throw new BackendValidationError(response.error || 'Failed to update notification subscription');
}
